import React, { useEffect, useRef, useState } from 'react';
import {
  Button,
  Dropdown,
  DropdownSkeleton,
  TooltipIcon,
  InlineLoading
} from 'carbon-components-react';
import { HelpFilled16, Maximize16 } from '@carbon/icons-react';
import { phoneNumberFormatter } from 'utilities/validation';
import { SCREEN_SIZE } from 'types';
import { ReactComponent as UserServiceDesk } from 'assets/icons/user--service-desk.svg';
import {
  HTMLViewer,
  ImageViewer,
  PDFViewer,
  IFrameViewer,
  DWFViewer,
  WaitingViewer
} from 'components';
import TextWithLinks from 'components/TextWithLinks';

export const ResponsesContent: React.FC<Props | any> = (props: Props) => {
  const {
    showDMFViewer,
    setShowDMFViewer,
    selectedReferralObj,
    screenSize,
    loadingEmailPreview,
    viewOrDownloadDropdownItem,
    handleViewOrDownloadDropdownChange,
    previewData,
    handleExpandClick,
    initialSelectedViewOrDownloadItem,
    isLoadingPreviewer,
    handleContactsIconClick,
    getPlanResponsesAboutRef,
    enquiryInformationRef,
    isModal,
    isFromSwxLocate,
    handleContactsSelection,
    inboundEmailId,
    enquiryId,
    selectionSource,
    hasSpatialDataLink,
    onDownloadSpatialDataClick,
    onViewResponseClick
  } = props;

  const [
    safetyMessageContainerHeight,
    setSafetyMessageContainerHeight
  ]: any = useState();
  const [
    contentMenusContainerHeight,
    setContentMenusContainerHeight
  ]: any = useState();
  const [
    enquiryInformationContainerHeight,
    setEnquiryInformationContainerHeight
  ]: any = useState();

  const [
    aboutResponsesContainerHeight,
    setAboutResponsesContainerHeight
  ]: any = useState();

  const [viewerContentHeight, setViewerContentHeight]: any = useState();
  const [contactDropdownItems, setContactDropdownItems] = useState<any[]>([]);
  const safetyMessageRef = useRef<HTMLDivElement>(null);
  const contentMenusRef = useRef<HTMLDivElement>(null);

  useEffect(
    () => {
      setSafetyMessageContainerHeight(
        safetyMessageRef.current?.offsetHeight || 0
      );
      setContentMenusContainerHeight(
        contentMenusRef.current?.offsetHeight || 0
      );
      setEnquiryInformationContainerHeight(
        enquiryInformationRef.current?.offsetHeight || 0
      );
      setAboutResponsesContainerHeight(
        getPlanResponsesAboutRef?.current?.offsetHeight || 0
      );
      /*eslint-disable*/
    },
    [
      safetyMessageRef.current?.offsetHeight,
      contentMenusRef.current?.offsetHeight,
      enquiryInformationRef.current?.offsetHeight,
      getPlanResponsesAboutRef?.current?.offsetHeight
    ] /*eslint-disable*/
  );

  useEffect(() => {
    const viewerFooter = !isFromSwxLocate ? 6 : 0;
    const mainHeader = !isFromSwxLocate
      ? 47
      : safetyMessageContainerHeight === 0
      ? 28
      : 44;
    const viewerContentHeight: string = `calc(${
      !isFromSwxLocate
        ? isModal
          ? safetyMessageContainerHeight === 0
            ? '95vh'
            : '93vh'
          : '94vh'
        : '100vh'
    } - (${aboutResponsesContainerHeight}px + ${contentMenusContainerHeight}px + ${enquiryInformationContainerHeight}px + ${safetyMessageContainerHeight}px + ${viewerFooter}px + ${mainHeader}px))`;
    setViewerContentHeight(viewerContentHeight);
    // eslint-disable-next-line
  }, [safetyMessageContainerHeight, aboutResponsesContainerHeight]);

  useEffect(() => {
    if (selectedReferralObj) {
      const options = [];
      if (selectedReferralObj.contactPhoneNumber) {
        options.push({
          label: (
            <div className="items">
              PHONE (GENERAL)
              <div>
                {phoneNumberFormatter({
                  phoneNumber: selectedReferralObj.contactPhoneNumber
                }).getNationalNumberFormat()}
              </div>
            </div>
          ),
          value: `phone|${selectedReferralObj.contactPhoneNumber}`
        });
      }

      if (selectedReferralObj.contactEmail) {
        options.push({
          label: (
            <div>
              EMAIL (GENERAL)
              <div className="email">{selectedReferralObj.contactEmail}</div>
            </div>
          ),
          value: `email|${selectedReferralObj.contactEmail}`
        });
      }

      if (selectedReferralObj.contactUrl) {
        options.push({
          label: (
            <div>
              WEB (GENERAL)
              <div>Opens in a new tab</div>
            </div>
          ),
          value: `web|${selectedReferralObj.contactUrl}`
        });
      }

      if (selectedReferralObj.contactEmergencyPhone) {
        options.push({
          label: (
            <div style={{ color: 'red' }}>
              EMERGENCY
              <div>
                {phoneNumberFormatter({
                  phoneNumber: selectedReferralObj.contactEmergencyPhone
                }).getNationalNumberFormat()}
              </div>
            </div>
          ),
          value: `emergency|${selectedReferralObj.contactEmergencyPhone}`
        });
      }

      setContactDropdownItems(options);
    }
  }, [selectedReferralObj]);

  const attachmentDropdownTooltip = (
    <TooltipIcon
      tooltipText={'Access the response documents directly'}
      className="tooltip enquiry-details-tooltip"
    >
      <HelpFilled16 className="tooltip-icon" />
    </TooltipIcon>
  );
  const attachmentDropdownTitleText = (
    <div className="title-text">
      <div>View or download</div>
      <div>{attachmentDropdownTooltip}</div>
    </div>
  );

  const contactsDropdownTooltip = (
    <TooltipIcon
      tooltipText={'Active contact links for this member'}
      className="tooltip enquiry-details-tooltip"
    >
      <HelpFilled16 className="tooltip-icon" />
    </TooltipIcon>
  );
  const contactsDropdownTitleText = (
    <div className="title-text">
      <div>Contact</div>
      <div>{contactsDropdownTooltip}</div>
    </div>
  );

  const handleContacts = (selected: string) => {
    handleContactsSelection(selected);
  };

  const disabledContact = () => {
    return (
      !Array.isArray(contactDropdownItems) || contactDropdownItems.length === 0
    );
  };

  return (
    <div className="get-plans-responses-content">
      {selectedReferralObj?.notices?.length > 0 &&
        selectedReferralObj?.status !== 'COMPLETED' && (
          <div className="get-plans-responses-content-announcements">
            {selectedReferralObj?.notices?.map((notice: any) => {
              return (
                <div className="get-plans-responses-content-announcements-announcement">
                  <div className="announcement-title">{notice.title}</div>
                  <div className="announcement-text">{notice.text}</div>
                </div>
              );
            })}
          </div>
        )}
      <div className="get-plans-responses-content-menus" ref={contentMenusRef}>
        <div className="get-plans-responses-content-menus-referral">
          <div
            className={
              selectionSource === 'interactive-map'
                ? 'get-plans-responses-content-menus-referral-content'
                : undefined
            }
          >
            <div className="get-plans-responses-content-menus-referral-logo">
              {selectedReferralObj?.logo ? (
                <img
                  src={`${process.env.REACT_APP_RSOTF_API_URL}/community/organisations/${selectedReferralObj.organisationId}/logo`}
                  alt=""
                  className="ao-logo"
                  title={selectedReferralObj.name}
                />
              ) : (
                <div className="ao-name" title={selectedReferralObj.name}>
                  {selectedReferralObj.name}
                </div>
              )}
            </div>
            <div className="get-plans-responses-content-menus-referral-id">
              {`Referral ID: ${selectedReferralObj?.externalId}`}
            </div>
          </div>
          {screenSize === SCREEN_SIZE.SM && (
            <div className="icon-circle-container">
              <div
                className={
                  disabledContact() ? 'icon-circle-disabled' : 'icon-circle'
                }
                onClick={disabledContact() ? '' : handleContactsIconClick}
              >
                <UserServiceDesk width={16} height={16} />
              </div>
              {selectedReferralObj.status === 'COMPLETED' && (
                <div
                  className={`icon-circle ${
                    previewData?.type === 'link' ||
                    previewData?.type === 'unknown'
                      ? 'disable-expand'
                      : ''
                  }`}
                  id="expand"
                  onClick={
                    previewData?.type === 'link' ||
                    previewData?.type === 'unknown'
                      ? undefined
                      : handleExpandClick
                  }
                >
                  <Maximize16 />
                </div>
              )}
            </div>
          )}
        </div>
        <div className="get-plans-responses-content-menus-options">
          {selectionSource === 'interactive-map' ? (
            <div className="get-plans-responses-content-menus-options-interactive-map-actions">
              {hasSpatialDataLink && (
                <Button
                  kind="secondary"
                  size="small"
                  onClick={onDownloadSpatialDataClick}
                  className="get-plans-responses-content-download-spatial-button"
                >
                  Download spatial data
                </Button>
              )}
              <Button
                kind="secondary"
                size="small"
                onClick={onViewResponseClick}
                className="get-plans-responses-content-view-response-button"
              >
                View response
              </Button>
              {selectedReferralObj.status === 'COMPLETED' && (
                <div className="get-plans-responses-content-menus-options-expand">
                  <div
                    className={`icon-circle ${
                      previewData?.type === 'link' ||
                      previewData?.type === 'unknown'
                        ? 'disable-expand'
                        : ''
                    }`}
                    onClick={
                      previewData?.type === 'link' ||
                      previewData?.type === 'unknown'
                        ? undefined
                        : handleExpandClick
                    }
                  >
                    <Maximize16 />
                  </div>
                </div>
              )}
            </div>
          ) : (
            <>
              <div className="get-plans-responses-content-menus-options-attachments">
                {!loadingEmailPreview &&
                  selectedReferralObj.status === 'COMPLETED' &&
                  viewOrDownloadDropdownItem.length > 0 && (
                    <Dropdown
                      id="attachments"
                      titleText={attachmentDropdownTitleText}
                      label="Select"
                      size="xl"
                      className="dropdown attachment-dropdown"
                      selectedItem={initialSelectedViewOrDownloadItem}
                      onChange={(item: any) =>
                        handleViewOrDownloadDropdownChange(
                          item?.selectedItem?.value
                        )
                      }
                      items={viewOrDownloadDropdownItem}
                    />
                  )}

                {loadingEmailPreview && (
                  <DropdownSkeleton className="dropdown-skeleton" />
                )}
              </div>
              {screenSize !== SCREEN_SIZE.SM && (
                <>
                  <div
                    className={`get-plans-responses-content-menus-options-contacts`}
                  >
                    {!loadingEmailPreview ? (
                      <Dropdown
                        id="organisationContacts"
                        titleText={contactsDropdownTitleText}
                        label="Select"
                        size="xl"
                        className="dropdown"
                        selectedItem={'Select'}
                        onChange={(item: any) =>
                          handleContacts(item?.selectedItem?.value)
                        }
                        disabled={
                          !Array.isArray(contactDropdownItems) ||
                          contactDropdownItems.length === 0
                        }
                        items={contactDropdownItems || []}
                      />
                    ) : (
                      <DropdownSkeleton className="dropdown-skeleton" />
                    )}
                  </div>
                  {selectedReferralObj.status === 'COMPLETED' && (
                    <div className="get-plans-responses-content-menus-options-expand">
                      <div
                        className={`icon-circle ${
                          previewData?.type === 'link' ||
                          previewData?.type === 'unknown'
                            ? 'disable-expand'
                            : ''
                        }`}
                        onClick={
                          previewData?.type === 'link' ||
                          previewData?.type === 'unknown'
                            ? undefined
                            : handleExpandClick
                        }
                      >
                        <Maximize16 />
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
      <div className="get-plans-responses-content-viewer">
        <div
          className="get-plans-responses-content-viewer-previewer"
          style={{
            height: viewerContentHeight || '24rem',
            minHeight: '24rem',
            overflow: 'auto',
            position: 'relative',
            width: '100%'
          }}
        >
          {loadingEmailPreview && (
            <div
              style={{
                display: 'flex',
                height: `calc(${viewerContentHeight} - 3rem)`,
                minHeight: '24rem',
                justifyContent: 'center',
                alignItems: 'center'
              }}
            >
              <InlineLoading
                style={{ justifyContent: 'center' }}
                description="Preparing response view ..."
              />
            </div>
          )}
          {!loadingEmailPreview &&
            previewData?.type === 'webMap' &&
            previewData?.body && (
              <IFrameViewer
                className="get-plans-responses-content-viewer-previewer-webmap"
                source={previewData?.body}
                selectedData={selectedReferralObj}
                title={'Web map'}
                viewerContentHeight={viewerContentHeight}
              />
            )}
          {!loadingEmailPreview &&
            previewData?.type === 'html' &&
            previewData?.body && (
              <HTMLViewer
                html={previewData?.body}
                title="Response body"
                inboundEmailId={inboundEmailId}
                referralId={selectedReferralObj?.externalId}
                enquiryId={enquiryId}
              />
            )}
          {!loadingEmailPreview &&
            previewData?.type === 'image' &&
            previewData?.body && (
              <ImageViewer
                fileId={previewData?.id}
                enquiryId={enquiryId}
                imageSource={previewData?.body}
                imageName={previewData?.name}
              />
            )}
          {!loadingEmailPreview &&
            previewData?.type === 'pdf' &&
            previewData?.body &&
            !isLoadingPreviewer && (
              <PDFViewer
                fileId={previewData?.id}
                enquiryId={enquiryId}
                fileUrl={previewData?.body}
                fileName={previewData?.name}
                viewerContentHeight={viewerContentHeight}
              />
            )}
          {!loadingEmailPreview &&
            previewData?.type === 'dwf' &&
            previewData?.body && (
              <DWFViewer
                showViewer={showDMFViewer}
                setShowViewer={setShowDMFViewer}
                title={previewData?.name}
                fileId={previewData?.id}
                enquiryId={enquiryId}
                dataBody={previewData?.body}
              />
            )}
          {selectedReferralObj.status !== 'COMPLETED' && (
            <WaitingViewer
              waittime={selectedReferralObj.waittime}
              title="The selected response is not ready"
            />
          )}
        </div>
      </div>
      {selectedReferralObj?.responses?.[0]?.custom_properties?.safetyMessage ||
        (selectedReferralObj?.safetyMessage && (
          <div ref={safetyMessageRef}>
            <TextWithLinks
              className="get-plans-responses-safety-message"
              text={
                selectedReferralObj?.responses?.[0]?.custom_properties
                  ?.safetyMessage || selectedReferralObj?.safetyMessage
              }
            />
          </div>
        ))}
    </div>
  );
};

type ownProps = {
  showDMFViewer: boolean;
  setShowDMFViewer: Function;
  isFromSwxLocate?: boolean;
  selectedReferralObj: any;
  selectedReferral: Number;
  screenSize: string;
  loadingEmailPreview: boolean;
  viewOrDownloadDropdownItem: any;
  handleViewOrDownloadDropdownChange: Function;
  previewData: any;
  handleExpandClick: any;
  initialSelectedViewOrDownloadItem: any;
  isLoadingPreviewer: boolean;
  handleContactsIconClick: any;
  getPlanResponsesAboutRef: any;
  enquiryInformationRef: any;
  isModal: boolean;
  handleContactsSelection: Function;
  inboundEmailId: number;
  enquiryId?: number;
  selectionSource?: 'interactive-map' | 'responses';
  hasSpatialDataLink?: boolean;
  onDownloadSpatialDataClick?: () => void;
  onViewResponseClick?: () => void;
};

type Props = ownProps;

ResponsesContent.displayName = 'ResponsesContent';

export default React.memo(ResponsesContent);
